/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { Button } from "@/components/ui/button";
import { DemoRequestButton } from "@/components/landing/demo-request-form";
import { isWaitlistMode, openWaitlist } from "@/lib/utils/waitlist-mode";
import { ArrowRight, CheckCircle } from "lucide-react";

const benefits = [
  "Plans from $29/mo",
  "Set up in minutes",
  "No long-term contracts",
  "Cancel anytime",
];

export function FinalCTASection() {
  return (
    <>
      <section
        className="relative py-16 md:py-24 bg-background-warm overflow-hidden border-t border-border/40"
        aria-label="Final call to action"
      >
        {/* Subtle blueprint grid */}
        <div
          className="blueprint-grid-gold absolute inset-0"
          aria-hidden="true"
        />

        <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <header className="max-w-3xl mx-auto text-center">
            {/* Heading */}
            <h2 className="text-[clamp(24px,3.5vw,36px)] leading-[1.1] font-bold font-display mb-4 text-foreground">
              Your whole business.{" "}
              <span className="text-highlight">One simple platform.</span>
            </h2>

            {/* Subheading */}
            <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-4 leading-relaxed">
              Estimates, proposals, projects, costs, invoices — connected. See
              what it looks like when everything just works together.
            </p>

            {/* CTA Buttons */}
            <nav
              className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-5 md:mb-6"
              aria-label="Call to action"
            >
              <div className="hover:scale-[1.03] active:scale-[0.97] transition-transform">
                <Button
                  size="pill"
                  onClick={() => {
                    isWaitlistMode()
                      ? openWaitlist()
                      : (window.location.href = "/auth/sign-up");
                  }}
                  className="bg-secondary hover:bg-secondary/90 text-white font-semibold shadow-2xl hover:shadow-secondary/50 transition-all"
                >
                  {isWaitlistMode() ? "Join the Waitlist" : "Get Started"}
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
              <DemoRequestButton
                variant="outline"
                size="pill"
                className="border-border hover:bg-muted/50 text-foreground"
              >
                Book a Demo
              </DemoRequestButton>
            </nav>

            {/* Benefits List */}
            <ul
              className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto text-sm"
              role="list"
            >
              {benefits.map((benefit) => (
                <li
                  key={benefit}
                  className="flex items-center gap-3 text-left text-muted-foreground"
                >
                  <CheckCircle
                    className="w-4 h-4 text-secondary flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </header>
        </div>
      </section>
    </>
  );
}
